// Kit pieces the city screens share: the resource HUD, title band, screen hero, now/next tiles, level track, amount slider,
// stepper, toggle, coordinate fields, live countdowns, tier discs and pips, cost lines, sticky action bar, collapsible card.
import { useEffect, useId, useState, type ButtonHTMLAttributes, type ReactNode } from "react";
import type { Village } from "@/api/types";
import { RESOURCE_KEYS, RESOURCE_NAMES, TIER_NUMERALS, TROOP_NAMES, TROOP_SLUG, type ResourceKey, type TroopKey } from "@/game/constants";
import { abbrev, fmtNum } from "@/game/format";
import { fmtDuration } from "@/game/time";
import { useNow } from "@/state/clock";
import { art } from "./art";
import { IconChevronRight, IconClock, IconGear, IconHelp, IconLock, IconMinus, IconPeople, IconPlus } from "./icons";
import { Bar, Card, Disc, Medallion, StatBand, cx } from "./primitives";
import { TitleBar } from "./chrome";

// ------------------------------------------------------------------ the resource HUD

export const ResourceBar = ({ v }: { v: Village | undefined }) => {
  const cap = v?.resources_capacity ?? 0;
  const boosted = (r: ResourceKey) => !!v?.active_effects?.[`boost_${r === "r_w" ? "wood" : r === "r_c" ? "clay" : "iron"}`];
  return (
    <div className="c-hud" role="group" aria-label="Resources">
      {RESOURCE_KEYS.map((r) => (
        <span key={r} className={cx("c-hud-item", v && (v.resources?.[r] ?? 0) >= cap && cap > 0 && "full")} title={`${RESOURCE_NAMES[r]} ${fmtNum(v?.resources?.[r])} · ${fmtNum(v?.resources_production?.[r])}/hr`}>
          <img src={art.hud[r === "r_w" ? "wood" : r === "r_c" ? "clay" : "iron"]} alt={RESOURCE_NAMES[r]} />
          {boosted(r) && <i className="bolt" aria-label="boosted" />}
          <b>{v ? abbrev(v.resources?.[r]) : "—"}</b>
        </span>
      ))}
      <span className="c-hud-item" title={`Warehouse capacity ${fmtNum(cap)}`}>
        <img src={art.icon.castle} alt="Storage" />
        <b>{v ? abbrev(cap) : "—"}</b>
      </span>
      <span className="c-hud-item wide" title={`Population ${fmtNum(v?.current_population)} of ${fmtNum(v?.max_population)}`}>
        <img src={art.hud.food} alt="Population" />
        <b>{v ? `${abbrev(v.current_population)}/${abbrev(v.max_population)}` : "—"}</b>
      </span>
    </div>
  );
};

// ------------------------------------------------------------------ title band (Barracks, Wheel): back · TITLE · gear/info

/** The live game's title bar (Classic Frame) with the old band's small subtitle. */
export const TitleBand = ({ title, sub, onBack, right, onRight, rightLabel }: {
  title: ReactNode; sub?: ReactNode; onBack?: () => void; right?: "gear" | "info" | ReactNode; onRight?: () => void; rightLabel?: string;
}) => (
  <div className="tframe">
    <TitleBar title={title} sub={sub} onBack={onBack}
      action={right ? { label: rightLabel ?? (right === "gear" ? "Settings" : "About"), icon: right === "gear" ? <IconGear /> : right === "info" ? <IconHelp /> : right, onClick: onRight } : undefined} />
  </div>
);

// ------------------------------------------------------------------ the screen frame: title bar + what the hero said

/** Was the painted screen hero; since the Classic Frame (Oct 10 2026) it is the live game's title bar — back · TITLE ·
 *  gear — and, when the screen has something to say, a slim strip under it: the line, the screen's own bits (tags,
 *  Claim all…) and the stat band. The painting props are kept so callers don't change; nothing is painted. */
export const ScreenHero = ({ level, title, blurb, stats, onBack, onGear, gearLabel = "Settings", gearIcon, children, className }: {
  level?: number | null; title: ReactNode; blurb?: ReactNode; art?: string; artWidth?: string; artPos?: string;
  stats?: { label: ReactNode; value: ReactNode; tone?: "crim" | "green"; small?: ReactNode }[];
  onBack?: () => void; onGear?: () => void; gearLabel?: string; /** another glyph in the top-right disc (a quill, say) */ gearIcon?: ReactNode; children?: ReactNode; className?: string;
  cutout?: boolean;
}) => (
  <div className={cx("tframe", className)}>
    <TitleBar title={title} level={level} onBack={onBack} action={onGear ? { label: gearLabel, icon: gearIcon, onClick: onGear } : undefined} />
    {(blurb || children || stats) && (
      <div className="tstrip">
        {blurb && <p className="blurb">{blurb}</p>}
        {children}
        {stats && <StatBand stats={stats} />}
      </div>
    )}
  </div>
);

/** The gold-ringed level disc used by heroes and the city's plates. */
export const LevelDisc = ({ level, size = "md", dim }: { level: number | string; size?: "sm" | "md"; dim?: boolean }) => (
  <span className={cx("c-lvl", size === "sm" && "sm", dim && "dim")}>{level}</span>
);

// ------------------------------------------------------------------ now / next tiles and the level track

export const NowNext = ({ nowLabel, nextLabel, now, next, unit, delta, maxed, nextNote }: {
  nowLabel: ReactNode; nextLabel?: ReactNode; now: ReactNode; next?: ReactNode; unit?: ReactNode; delta?: ReactNode; maxed?: boolean; nextNote?: ReactNode;
}) => (
  <div className="c-nownext">
    <div className="c-tile now">
      <div className="lbl">{nowLabel}</div>
      <div className="val">{now}{unit && <small> {unit}</small>}</div>
    </div>
    <span className="c-arrow" aria-hidden="true"><i /><IconChevronRight /></span>
    <div className={cx("c-tile next", maxed && "maxed")}>
      <div className="lbl">{nextLabel ?? (maxed ? "Final level" : "Next")}</div>
      <div className="val gold">{maxed ? "MAX" : next}{!maxed && unit && <small> {unit}</small>}</div>
      {!maxed && delta && <div className="delta">{delta}</div>}
      {nextNote && <div className="delta muted">{nextNote}</div>}
    </div>
  </div>
);

export const LevelTrack = ({ level, max }: { level: number; max: number }) => (
  <div className="c-track" aria-label={`Level ${level} of ${max}`}>
    <span className="lbl">Level</span>
    <span className="ticks" style={{ ["--n" as string]: max }}>
      {Array.from({ length: max }, (_, i) => <i key={i} className={cx(i < level && "on", i === level && "next")} />)}
    </span>
    <span className="val">{level} / {max}</span>
  </div>
);

// ------------------------------------------------------------------ amount slider + field + MAX

export const AmountSlider = ({ value, max, onChange, label, maxHint = true, disabled, step = 1, min = 0 }: {
  value: number; max: number; onChange: (n: number) => void; label?: string; maxHint?: boolean; disabled?: boolean; step?: number; min?: number;
}) => {
  const id = useId();
  const clamp = (n: number) => Math.max(min, Math.min(max, Math.round(n / step) * step));
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  const pct = max > min ? ((value - min) / (max - min)) * 100 : 0;
  return (
    <div className={cx("c-amount", disabled && "disabled")}>
      <input
        id={id}
        type="range"
        className="c-range"
        min={min}
        max={Math.max(min, max)}
        step={step}
        value={Math.min(value, Math.max(min, max))}
        disabled={disabled || max <= min}
        onChange={(e) => onChange(clamp(Number(e.target.value)))}
        aria-label={label ?? "Amount"}
        style={{ ["--p" as string]: `${pct}%` }}
      />
      <input
        type="text"
        inputMode="numeric"
        className="c-num"
        value={text}
        disabled={disabled}
        onChange={(e) => {
          const t = e.target.value.replace(/[^\d]/g, "");
          setText(t);
          if (t !== "") onChange(clamp(Number(t)));
        }}
        onBlur={() => setText(String(value))}
        aria-label={`${label ?? "Amount"} number`}
      />
      <div className="c-maxwrap">
        <button type="button" className="btn md c-max" disabled={disabled || max <= min} onClick={() => onChange(max)}>Max</button>
        {maxHint && <span className="hint">{fmtNum(max)}</span>}
      </div>
    </div>
  );
};

/** Gold slider alone (Academy's buffers, Market's resource rows). */
export const GoldRange = ({ value, max, onChange, min = 0, step = 1, label, disabled }: { value: number; max: number; onChange: (n: number) => void; min?: number; step?: number; label?: string; disabled?: boolean }) => {
  const pct = max > min ? ((value - min) / (max - min)) * 100 : 0;
  return (
    <input type="range" className="c-range" min={min} max={Math.max(min, max)} step={step} value={Math.min(value, Math.max(min, max))} disabled={disabled || max <= min}
      onChange={(e) => onChange(Number(e.target.value))} aria-label={label ?? "Amount"} style={{ ["--p" as string]: `${pct}%` }} />
  );
};

export const Stepper = ({ value, onChange, min = 0, max = Infinity, step = 1, format, label, disabled }: {
  value: number; onChange: (n: number) => void; min?: number; max?: number; step?: number; format?: (n: number) => ReactNode; label?: string; disabled?: boolean;
}) => (
  <div className="c-stepper" role="group" aria-label={label}>
    <button type="button" aria-label="Less" disabled={disabled || value <= min} onClick={() => onChange(Math.max(min, value - step))}><IconMinus /></button>
    <span className="val">{format ? format(value) : fmtNum(value)}</span>
    <button type="button" aria-label="More" disabled={disabled || value >= max} onClick={() => onChange(Math.min(max, value + step))}><IconPlus /></button>
  </div>
);

export const Toggle = ({ on, onChange, label, disabled, busy }: { on: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean; busy?: boolean }) => (
  <button type="button" role="switch" aria-checked={on} aria-label={label} className={cx("c-toggle", on && "on")} disabled={disabled || busy} onClick={() => onChange(!on)}>
    <span className="word">{on ? "On" : "Off"}</span>
    <span className="knob">{busy ? <span className="spin" /> : on ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg> : null}</span>
  </button>
);

export const XYField = ({ x, y, onChange, label = "Target" }: { x: string; y: string; onChange: (x: string, y: string) => void; label?: string }) => (
  <div className="c-xy" role="group" aria-label={label}>
    <input className="input" inputMode="numeric" value={x} placeholder="X" aria-label="X" onChange={(e) => onChange(e.target.value.replace(/[^\d]/g, "").slice(0, 4), y)} />
    <span className="comma">,</span>
    <input className="input" inputMode="numeric" value={y} placeholder="Y" aria-label="Y" onChange={(e) => onChange(x, e.target.value.replace(/[^\d]/g, "").slice(0, 4))} />
  </div>
);

// ------------------------------------------------------------------ time

/** "2:00:29" counting down to `endMs` on the server clock; "done" when passed. */
export const Countdown = ({ endMs, className, done = "Done", compact }: { endMs: number; className?: string; done?: ReactNode; compact?: boolean }) => {
  const now = useNow();
  const left = endMs - now;
  if (!Number.isFinite(endMs)) return <span className={className}>—</span>;
  return <span className={cx("c-count", className)}>{left <= 0 ? done : fmtDuration(left, { compact })}</span>;
};

/** A bar that fills from startMs to endMs on the server clock. */
export const TimeBar = ({ startMs, endMs, color = "gold", thick }: { startMs: number; endMs: number; color?: "gold" | "green" | "crim"; thick?: boolean }) => {
  const now = useNow();
  const v = endMs > startMs ? (now - startMs) / (endMs - startMs) : 1;
  return <Bar value={Math.min(1, Math.max(0, v))} color={color} thick={thick} />;
};

// ------------------------------------------------------------------ tiers, pips, locks

export const TierDisc = ({ tier, className }: { tier: number; className?: string }) => (
  <span className={cx("c-tier", className)} aria-label={`Research ${TIER_NUMERALS[tier] || tier}`}>{TIER_NUMERALS[tier] ?? tier}</span>
);

export const Pips = ({ level, max = 3, boxes }: { level: number; max?: number; boxes?: boolean }) => (
  <span className={cx("c-pips", boxes && "boxes")} aria-label={`Level ${level} of ${max}`}>
    {Array.from({ length: max }, (_, i) => <i key={i} className={cx(i < level && "on")}>{boxes ? (i < level ? TIER_NUMERALS[i + 1] : "") : null}</i>)}
  </span>
);

export const LockSeal = ({ size = "md" }: { size?: "sm" | "md" | "lg" }) => (
  <span className={cx("c-lock", size)} aria-hidden="true"><IconLock /></span>
);

/** A troop face at its research tier, with the tier disc at the lower left; dim for none-home / locked. */
/** A unit's face in a gold ring with its research tier (tier 0 = unknown: no tier disc, as in reports). */
export const UnitMedal = ({ troop, tier, size = 60, dim, locked, glow, count }: { troop: TroopKey; tier: number; size?: number; dim?: boolean; locked?: boolean; glow?: boolean; count?: number }) => (
  <span className={cx("c-unit", dim && "dim", locked && "locked", glow && "glow")} style={{ ["--s" as string]: `calc(var(--u) * ${size})` }}>
    {/* Oct 11 2026: the painted troop icons, freestanding on the slate (no medallion ring) */}
    <img className="c-unit-img" src={art.unitIcon(TROOP_SLUG[troop])} alt={TROOP_NAMES[troop]} loading="lazy" decoding="async" />
    {locked ? <span className="c-unit-lock"><IconLock /></span> : troop !== "t_n" && tier > 0 ? <TierDisc tier={tier} className="c-unit-tier" /> : null}
    {count !== undefined && <span className="c-unit-count">{fmtNum(count)}</span>}
  </span>
);

export const ResMedal = ({ r, size = 60, glow, dim, onClick, selected, label }: { r: ResourceKey | "any"; size?: number; glow?: boolean; dim?: boolean; onClick?: () => void; selected?: boolean; label?: string }) => {
  const src = r === "any" ? art.store.buy_all : art.store[r === "r_w" ? "buy_wood" : r === "r_c" ? "buy_clay" : "buy_iron"];
  const m = <Medallion src={src} alt={label ?? (r === "any" ? "Any" : RESOURCE_NAMES[r])} size={size} glow={glow || selected} dim={dim} />;
  return onClick ? <button type="button" className={cx("c-pick", selected && "on")} aria-pressed={selected} onClick={onClick}>{m}</button> : m;
};

// ------------------------------------------------------------------ cost line: 60 wood · 30 clay · 20 iron · 1 pop · 2:15

export const CostLine = ({ cost, time, pop, short, className, size = "md" }: {
  cost: Partial<Record<ResourceKey, number>>; time?: number; pop?: number; short?: Partial<Record<ResourceKey, number>>; className?: string; size?: "sm" | "md";
}) => (
  <div className={cx("c-cost", size === "sm" && "sm", className)}>
    {RESOURCE_KEYS.map((r) => (
      <span key={r} className={cx(!!short?.[r] && "short")}>
        <img src={art.hud[r === "r_w" ? "wood" : r === "r_c" ? "clay" : "iron"]} alt={RESOURCE_NAMES[r]} />{fmtNum(cost[r] ?? 0)}
      </span>
    ))}
    {pop !== undefined && <span><IconPeople />{fmtNum(pop)}</span>}
    {time !== undefined && <span><IconClock />{fmtDuration(time)}</span>}
  </div>
);

// ------------------------------------------------------------------ sticky action bar (Rally send, Market send)

export const StickyBar = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={cx("c-sticky", className)}>{children}</div>
);

// ------------------------------------------------------------------ collapsible card ("SUPPORT EN ROUTE · None ›")

export const Collapsible = ({ title, aside, badge, open, onToggle, children }: { title: ReactNode; aside?: ReactNode; badge?: number; open: boolean; onToggle: () => void; children?: ReactNode }) => (
  <Card small className={cx("c-collapse", open && "open")}>
    <button type="button" className="c-collapse-head" aria-expanded={open} onClick={onToggle}>
      <h2>{title}{badge ? <span className="badge inline">{badge}</span> : null}</h2>
      {aside !== undefined && <span className="aside">{aside}</span>}
      <span className="disc xs" aria-hidden="true">{open ? <IconChevronDownSmall /> : <IconChevronRight />}</span>
    </button>
    {open && <div className="c-collapse-body">{children}</div>}
  </Card>
);
const IconChevronDownSmall = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>;

/** A small "+" / "MAX" / lock pill at the right of a building or research row. */
export const RowAction = ({ kind, onClick, label, disabled }: { kind: "plus" | "max" | "lock" | "minus"; onClick?: () => void; label?: string; disabled?: boolean } & ButtonHTMLAttributes<HTMLButtonElement>) => {
  if (kind === "max") return <span className="c-maxpill">MAX</span>;
  if (kind === "lock") return <span className="c-lockpill" aria-label={label ?? "Locked"}><IconLock /></span>;
  return (
    <Disc size="sm" label={label ?? (kind === "plus" ? "Upgrade" : "Lower")} className="c-rowdisc" onClick={onClick} disabled={disabled}>
      {kind === "plus" ? <IconPlus /> : <IconMinus />}
    </Disc>
  );
};
